import api from "./api";
import type {
  CreateCustomerData,
  Customer,
  CustomerCreateResponse,
  CustomerDetail,
  CustomerListResponse,
  CustomerDeleteResponse,
  UpdateCustomerData,
} from "../types/models";

export interface CustomerListParams {
  search?: string | null;
  page?: number;
  limit?: number;
  location_status?: string | null;
  /** pending_review | none (no pending portal request); admin/manager only */
  portal_account_request_status?: string | null;
}

/**
 * List customers with optional search, pagination, and filters. Story 4.2 / 10-6-4
 */
export async function getCustomers(params: CustomerListParams = {}): Promise<CustomerListResponse> {
  const { search, page = 1, limit = 20, location_status, portal_account_request_status } = params;
  const queryParams: Record<string, string | number> = { page, limit };
  if (search?.trim()) queryParams.search = search.trim();
  if (location_status?.trim()) queryParams.location_status = location_status.trim();
  if (portal_account_request_status?.trim()) {
    queryParams.portal_account_request_status = portal_account_request_status.trim();
  }
  const response = await api.get<CustomerListResponse>("/customers", {
    params: queryParams,
  });
  return response.data;
}

export interface GetCustomerParams {
  order_page?: number;
  order_limit?: number;
}

/**
 * Get a single customer by ID with paginated order_history and order_total. Story 4.2
 */
export async function getCustomer(
  id: string,
  params: GetCustomerParams = {}
): Promise<CustomerDetail> {
  const { order_page = 1, order_limit = 20 } = params;
  const response = await api.get<CustomerDetail>(`/customers/${id}`, {
    params: { order_page, order_limit },
  });
  return response.data;
}

/**
 * Create a new customer.
 * Response may include existing_customer_name when phone already exists (duplicate hint).
 */
export async function createCustomer(data: CreateCustomerData): Promise<CustomerCreateResponse> {
  const response = await api.post<CustomerCreateResponse>("/customers", data);
  return response.data;
}

/**
 * Update a customer by ID (partial). Story 4.2
 */
export async function updateCustomer(id: string, data: UpdateCustomerData): Promise<Customer> {
  const response = await api.put<Customer>(`/customers/${id}`, data);
  return response.data;
}

/**
 * Soft-delete a customer. Returns order_count for frontend warning. Story 4.2
 */
export async function deleteCustomer(id: string): Promise<CustomerDeleteResponse> {
  const response = await api.delete<CustomerDeleteResponse>(`/customers/${id}`);
  return response.data;
}
